import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { flagBioNudge } from "@/lib/bioNudge";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Image as ImageIcon, Video, Smile, Film, Music, Ban, CalendarClock, Church, Pencil, AtSign, Hash, HeartHandshake, Camera, HandHeart, Users, Eye } from "lucide-react";

const TESTIMONY_CATS = [
  { key: "healing", tKey: "testimony.cat_healing", fb: "Guérison" },
  { key: "provision", tKey: "testimony.cat_provision", fb: "Provision" },
  { key: "salvation", tKey: "testimony.cat_salvation", fb: "Salut" },
  { key: "family", tKey: "testimony.cat_family", fb: "Famille" },
  { key: "other", tKey: "testimony.cat_other", fb: "Autre" },
];
import { toast } from "sonner";
import { FileUpload, resolveMediaUrl, uploadMediaFile, generateVideoPosterUrl, captureVideoPoster, captureVideoPosterFromUrl, uploadPosterBlob } from "@/components/FileUpload";
import { isNativePlatform, pickVideoNative, pickMediaNative, uploadVideoNativeBackground } from "@/lib/nativeVideoUpload";
import { useUploadQueue } from "@/context/UploadQueueContext";
import { BG_GRADIENTS, BG_IMAGES } from "@/lib/gradients";
import { BgTextFrame, BG_TEXT_PAD, BG_TEXT_SHADOW, BG_TEXT_MAX_PX } from "@/components/BgTextFrame";
import { Slider } from "@/components/ui/slider";
import { useAutoFitText, DEFAULT_BG_TEXT_SCALE, bgTextMaxChars } from "@/lib/useAutoFitText";
import { useIsMobile } from "@/lib/useIsMobile";
import { phoneNav } from "@/lib/platform";
import { PC_COMPOSER_MODAL } from "@/lib/dialogSizes";
import { parseVideoEmbed, parseFirstUrl } from "@/lib/embeds";
import { avatarSrc } from "@/lib/defaults";
import { EmojiPicker, EmojiTray } from "@/components/composer/ComposerBits";
import { AlbumPreviews, SinglePreview, AudioPreview, MusicSection, ComposerLinkPreview } from "@/components/composer/PostMediaPreviews";
import { PostFullscreenComposer } from "@/components/composer/PostFullscreenComposer";
import { useMentionAutocomplete, MentionSuggestions } from "@/components/MentionText";
import { PostRichEditor } from "@/components/composer/PostRichEditor";
import { PostPreviewDialog } from "@/components/composer/PostPreview";
import { buildPreviewPost } from "@/lib/previewPost";
import { stripRichMarkers } from "@/lib/richFormat";
import { MediaFxEditor, useFxEditor, keepDialogOpenForFx } from "@/components/media/MediaFxEditor";

// Compatibilité : d'autres composants importent UploadOverlay depuis ce fichier.
import { maxDatetimeLocal } from "@/lib/scheduleLimits";
import { profileIncomplete, requireCompleteProfile } from "@/lib/profileDone";
import { readDefaultAudience, writeDefaultAudience, AUDIENCES, audienceOf, audienceAccent } from "@/lib/postAudience";
import { CloseFriendsLink, CloseFriendsDialog } from "@/components/CloseFriends";
import { useGuard } from "@/lib/guards";
import { ComposerGroupsSheet, describeDestinations } from "@/components/composer/ComposerGroupsSheet";
export { UploadOverlay } from "@/components/composer/ComposerBits";

// Reads an audio file's duration (seconds) from a URL/blob. Blob audio often
// reports Infinity on loadedmetadata, so we also wait for durationchange.
const readAudioDuration = (url) => new Promise((resolve) => {
  if (!url) { resolve(null); return; }
  const el = document.createElement("audio");
  el.preload = "metadata";
  let done = false;
  const finish = (v) => {
    if (done) return; done = true;
    el.removeEventListener("loadedmetadata", h);
    el.removeEventListener("durationchange", h);
    resolve(v);
  };
  const h = () => { if (isFinite(el.duration) && el.duration > 0) finish(el.duration); };
  el.addEventListener("loadedmetadata", h);
  el.addEventListener("durationchange", h);
  el.addEventListener("error", () => finish(null));
  setTimeout(() => finish(isFinite(el.duration) && el.duration > 0 ? el.duration : null), 3000);
  el.src = url;
});

let attSeq = 0;
const newId = () => `att-${Date.now()}-${attSeq++}`;
// Lot 583 — bouton d'action de la carte repliée (ligne 2) : icône + libellé court, réparti sur la largeur.
const COMPOSER_ACTION_CLS = "flex-1 min-w-0 h-10 flex items-center justify-center gap-1 rounded-xl px-0.5 text-[12px] sm:text-[13px] sm:gap-1.5 font-semibold hover:bg-[color:var(--yalola-surface-alt)] transition-colors";

// Facebook-style composer: collapsed card opens a "Créer une publication" modal (PC) or the
// fullscreen Instagram-style composer (phone web < 768 px / native app).
export const PostComposer = ({ onPublished, groupId, groupName = "", churchId = null, churchName = "", variant = "card" }) => {
  const { user, t, config } = useApp();
  const { enqueuePost } = useUploadQueue();
  const nav = useNavigate();
  // On Android, feed videos are picked with a native picker + uploaded via a native
  // background service (no in-memory file read → no OOM, keeps going when locked).
  // Images/audio keep the standard file input; the web keeps everything as-is.
  const native = isNativePlatform();
  const mediaAccept = native ? "image/*,audio/*" : "image/*,video/*,audio/*";
  const modalMediaAccept = native ? "image/*" : "image/*,video/*";
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  // attachments: {id, kind: image|video|audio, file, localUrl, localPoster, url, progress, status, width, height, duration}
  const [attachments, setAttachments] = useState([]);
  const [isReel, setIsReel] = useState(false);
  const [layout, setLayout] = useState(null); // "before_after" : 2 photos ou 2 vidéos comparées
  const [bg, setBg] = useState(null);
  // Story-style background music overlaid on the post's photo(s)/video.
  const [music, setMusic] = useState(null); // { url, title, start } | null
  const [keepVideoSound, setKeepVideoSound] = useState(false);
  const [musicVolume, setMusicVolume] = useState(0.7); // 0.35 soft · 0.7 medium · 1.0 loud
  const [showPalette, setShowPalette] = useState(false);
  const [textScale, setTextScale] = useState(1); // slider around the auto-fit size (0.6–1.4)
  const scaleTouched = useRef(false); // user moved the slider → keep their value when re-picking a background
  const bgCanvasRef = useRef(null);
  const bgTextareaRef = useRef(null);
  const plainTextareaRef = useRef(null);
  const isMobileSheet = useIsMobile("(max-width: 639px), (max-height: 520px)"); // sheet breakpoint (sm) + phone landscape
  // Téléphone (web < 768 px) et app native : composeur plein écran façon Instagram ; le PC garde la feuille.
  const phoneFs = useIsMobile("(max-width: 767px)") || phoneNav();
  const [emojiTray, setEmojiTray] = useState(false);
  // Lot 535 — caméra intégrée (téléphone) : chip « Caméra » du composeur plein écran, bouton du fil, ou arrivée depuis
  // les Reels (/home?camera=video) → composeur ouvert avec la caméra déjà lancée en mode vidéo.
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState("photo");
  const openCamera = (mode = "photo") => { setCameraMode(mode); setOpen(true); setCameraOpen(true); };
  // Lot 597 — profil incomplet : le composeur ne s'ouvre jamais (quelle que soit la porte d'entrée : « Quoi de neuf »,
  // photo, caméra, partage vers une publication…) ; la feuille « Complète ton profil » prend sa place.
  const gated = profileIncomplete(user);
  useEffect(() => {
    if (open && !requireCompleteProfile(user)) { setOpen(false); setCameraOpen(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const onCameraCapture = ({ file, fx, duration, width, height }) => { setCameraOpen(false); handleFiles([file], { fx, duration, width, height }); };
  useEffect(() => {
    if (!phoneFs || groupId || churchId || variant !== "card") return;
    const sp = new URLSearchParams(window.location.search);
    const cam = sp.get("camera");
    if (!cam) return;
    sp.delete("camera");
    window.history.replaceState(null, "", `${window.location.pathname}${sp.toString() ? `?${sp}` : ""}${window.location.hash}`);
    openCamera(cam === "video" ? "v15" : "photo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Picking a background starts the text at 60 % (same rule as Story); clearing it restores auto.
  const pickBg = (key) => {
    setBg(key);
    if (key && !scaleTouched.current) setTextScale(DEFAULT_BG_TEXT_SCALE);
    if (!key) { setTextScale(1); scaleTouched.current = false; }
  };
  // Controlled reveal of the field the emoji lands in — scrolls ONLY the sheet body, never the whole modal.
  const revealTextarea = () => {
    const el = bgTextareaRef.current || plainTextareaRef.current;
    try { el?.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch { /* noop */ }
  };
  const insertEmoji = (e) => {
    // Coloured background: the Facebook-style cap also applies to emojis inserted from the picker.
    setContent((c) => (bg && attachments.length === 0 && (c + e).length > bgMax ? c : c + e));
    revealTextarea();
  };
  // @mentions : membres tagués dans le texte ({id, name}) ; autocomplétion quand le texte finit par « @xx ».
  const [mentions, setMentions] = useState([]);
  const mentionAc = useMentionAutocomplete({ text: content, setText: setContent, onPicked: (u) => setMentions((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, u].slice(-20))) });
  // Puces « @Mention » / « #Hashtag » : ajoutent le symbole en fin de texte et redonnent le focus → la liste s'ouvre.
  const startMention = (sym = "@") => {
    setContent((c) => (c && !/\s$/.test(c) ? `${c} ${sym}` : `${c}${sym}`));
    const el = bgTextareaRef.current || plainTextareaRef.current;
    // <textarea> (fond coloré) : curseur en fin ; champ riche : le focus suit la valeur (PostRichEditor → fin du texte).
    setTimeout(() => { try { el?.focus(); if (el?.setSelectionRange) el.setSelectionRange(el.value.length, el.value.length); } catch { /* noop */ } }, 30);
    revealTextarea();
  };
  const startHashtag = () => startMention("#");
  const usedMentions = mentions.filter((m) => content.includes(`@${m.name}`));
  // Text on a coloured background stays readable: hard cap (admin-set, default 130) + live counter.
  const bgMax = bgTextMaxChars(config);
  const bgTooLong = content.length > bgMax;
  useEffect(() => { if (bg && bgTooLong && attachments.length === 0) pickBg(null); }, [bg, bgTooLong]); // eslint-disable-line react-hooks/exhaustive-deps
  // Le cadre monte APRÈS l'ouverture (portail Radix) : sa présence en state relance l'ajustement
  // → un brouillon restauré (fond + texte) est ajusté dès l'ouverture, pas seulement à la frappe.
  const [bgCanvasEl, setBgCanvasEl] = useState(null);
  const setBgCanvas = useCallback((el) => { bgCanvasRef.current = el; setBgCanvasEl(el); }, []);
  useAutoFitText({ boxRef: bgCanvasRef, textRef: bgTextareaRef, text: content, scale: textScale, maxPx: BG_TEXT_MAX_PX, isTextarea: true, active: !!bg && attachments.length === 0, refitKey: bgCanvasEl });
  const [posting, setPosting] = useState(false);
  const [previewPost, setPreviewPost] = useState(null); // lot 779 — aperçu avant publication
  // Preachers must explicitly choose a destination ("Moi" or one of their churches)
  // before they can publish — prevents accidental un-targeted posts. Gated by admin feature flag.
  const features = config?.settings?.features || {};
  const fEnabled = (k) => features[k] !== false;
  // Reel classification is driven by admin settings (global settings doc), served
  // via /config/public. Horizontal shorts become Reels when the admin allows it.
  const reelsHorizontal = config?.settings?.reels_horizontal !== false;
  const reelMaxSec = Number(config?.settings?.reel_max_seconds) || 180;
  const isPreacher = user?.role === "preacher" || !!user?.manages_church;  // lot 607 : gestionnaire délégué d'une page d'église
  const publishAsEnabled = fEnabled("preacher_publish_as");
  const rememberDest = fEnabled("publish_as_memory");
  const memKey = user ? `yalola.publish_as.${user.id}` : null;
  const [myChurches, setMyChurches] = useState([]);
  const [publishAs, setPublishAs] = useState(null); // null | "me" | churchId
  useEffect(() => {
    if (publishAsEnabled && isPreacher && !groupId && !churchId) {
      api.get("/churches/mine").then(({ data }) => setMyChurches(data.churches || [])).catch(() => setMyChurches([]));
    } else {
      setMyChurches([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishAsEnabled, isPreacher, groupId, churchId]);
  const needsPublishAs = publishAsEnabled && isPreacher && !groupId && !churchId && myChurches.length > 0;
  // Lot 649 — audience (Public / Abonnés / Abonnés mutuels / Suivis / Moi) : dernier choix mémorisé ; sans objet
  // dans un groupe ou au nom d'une page d'église (toujours publique).
  const [audience, setAudienceState] = useState(() => readDefaultAudience(user));
  const [closeFriendsOpen, setCloseFriendsOpen] = useState(false); // lot 655
  const setAudience = (v) => { setAudienceState(v); writeDefaultAudience(v); };
  const audienceEnabled = !groupId && !churchId && !(publishAs && publishAs !== "me");
  // Preselect the preacher's last destination to speed up publishing.
  useEffect(() => {
    if (!needsPublishAs || !rememberDest || publishAs || !memKey) return;
    try {
      const saved = localStorage.getItem(memKey);
      if (saved === "me" || myChurches.some((c) => c.id === saved)) setPublishAs(saved);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsPublishAs, rememberDest, myChurches, memKey, publishAs]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  // Lot 756 — « Où publier ? » : la même publication dans plusieurs groupes (+ mon fil) en une écriture. Depuis un groupe,
  // le groupe courant est verrouillé ; depuis le fil, « Mon fil » est coché par défaut. Hors pages d'église.
  const multiGroupsOn = useGuard("composer_multi_groups") && !churchId;
  const previewOn = useGuard("composer_post_preview");   // lot 780 — interrupteur / repli de l'aperçu (lot 779)
  const [destinations, setDestinations] = useState({ personal: !groupId, groups: [] });
  const [groupsSheet, setGroupsSheet] = useState(false);
  const destCount = destinations.groups.length + (groupId ? 1 : 0) + (destinations.personal ? 1 : 0);
  const destLabel = multiGroupsOn ? describeDestinations(destinations, groupId ? groupName : "", t) : "";
  // Destination effective : group_id = groupe courant, sinon (fil sans « Mon fil ») le 1er groupe coché ; group_ids = le reste.
  const resolveDest = () => {
    if (!multiGroupsOn || !destinations.groups.length) return { group_id: groupId || null, group_ids: null };
    const ids = destinations.groups.map((g) => g.id);
    if (groupId) return { group_id: groupId, group_ids: ids };
    if (destinations.personal) return { group_id: null, group_ids: ids };
    return { group_id: ids[0], group_ids: ids.slice(1) };
  };
  // « Témoignage » : la publication apparaît aussi dans la rubrique Témoignages (post_type=testimony). Hors groupes / églises.
  const [asTestimony, setAsTestimony] = useState(false);
  const [testimonyCat, setTestimonyCat] = useState("other");
  const testimonyAllowed = !groupId && !churchId && config?.settings?.features?.testimonies !== false;
  const testimonyOn = asTestimony && testimonyAllowed;
  const minDatetimeLocal = () => { const dt = new Date(Date.now() + 5 * 60000); return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
  const [linkPreview, setLinkPreview] = useState(null);
  const dismissedLinkRef = useRef(null);
  const fetchedLinkRef = useRef(null);
  const reelToastedRef = useRef(false);
  const draftLoadedRef = useRef(false);
  const previewVideoRef = useRef(null);
  const draftKey = `yalola.draft.${groupId || "home"}`;

  // Draft: restore on mount (text + already-uploaded media survive an accidental close or reload).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.content) setContent(d.content);
        if (Array.isArray(d.mentions)) setMentions(d.mentions.filter((m) => m && m.id && m.name).slice(0, 20));
        if (Array.isArray(d.attachments) && d.attachments.length) {
          setAttachments(d.attachments.map((a) => ({
            id: newId(), kind: a.kind, file: null, localUrl: null, url: a.url,
            progress: 100, status: "done", width: a.width, height: a.height, duration: a.duration, caption: a.caption || "", fx: a.fx || null,
          })));
        }
        if (d.bg && (d.content || "").length <= bgMax) setBg(d.bg);
        if (typeof d.textScale === "number") setTextScale(d.textScale);
        if (d.isReel) { setIsReel(true); reelToastedRef.current = true; }
        if (d.music && d.music.url) setMusic(d.music);
        if (d.keepVideoSound) setKeepVideoSound(true);
        if (typeof d.musicVolume === "number") setMusicVolume(d.musicVolume);
      }
    } catch { /* noop */ }
    draftLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft: persist on every change (only finished uploads are kept).
  useEffect(() => {
    if (!draftLoadedRef.current) return;
    const done = attachments.filter((a) => a.status === "done" && a.url)
      .map(({ kind, url, width, height, duration, caption, fx }) => ({ kind, url, width, height, duration, caption, fx: fx || null }));
    if (!content.trim() && done.length === 0 && !bg) {
      localStorage.removeItem(draftKey);
      return;
    }
    localStorage.setItem(draftKey, JSON.stringify({ content, attachments: done, bg, textScale, isReel, music, keepVideoSound, musicVolume, mentions: usedMentions }));
  }, [content, attachments, bg, textScale, isReel, music, keepVideoSound, musicVolume, draftKey, mentions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Facebook-style link preview: video links (YouTube/FB) or generic article links.
  useEffect(() => {
    const video = parseVideoEmbed(content);
    const rawUrl = video?.url || parseFirstUrl(content);
    if (!rawUrl || attachments.length > 0) {
      setLinkPreview(null);
      fetchedLinkRef.current = null;
      return;
    }
    if (dismissedLinkRef.current === rawUrl || fetchedLinkRef.current === rawUrl) return;
    const timer = setTimeout(async () => {
      fetchedLinkRef.current = rawUrl;
      try {
        const { data } = await api.get("/link-preview", { params: { url: rawUrl } });
        setLinkPreview(data.preview);
      } catch {
        setLinkPreview(video ? { ...video, title: null, author: null, thumbnail: null } : null);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, attachments.length]);

  const removeLinkPreview = () => {
    dismissedLinkRef.current = linkPreview?.url || parseVideoEmbed(content)?.url || parseFirstUrl(content) || null;
    setLinkPreview(null);
  };

  const firstName = user?.first_name || user?.name?.split(" ")[0] || "";
  const placeholder = t("composer.whats_new", "Quoi de neuf, {name} ?").replace("{name}", firstName);

  const imageAtts = attachments.filter((a) => a.kind === "image");
  const videoAtts = attachments.filter((a) => a.kind === "video");
  const videoAtt = videoAtts[0] || null; // first video (compat: preview ref, poster, video_url)
  const audioAtt = attachments.find((a) => a.kind === "audio");
  const visualAtts = attachments.filter((a) => a.kind === "image" || a.kind === "video"); // add order
  const hasMedia = attachments.length > 0;
  const visualCount = visualAtts.length;
  const hasVisual = visualCount > 0;
  // Album caps come from admin settings (media_limits) with sensible defaults.
  const ml = config?.settings?.media_limits || {};
  const MAX_MEDIA = Math.max(2, Number(ml.post_album_photo_max) || 10); // pure-photo album cap
  const MAX_VIDEOS = Math.max(1, Number(ml.post_video_max) || 2);        // videos per post
  const MAX_MIXED = Math.max(2, Number(ml.post_album_mixed_max) || 2);   // total items when a video is present
  // A visual post is an ALBUM once it holds 2+ media (photos and/or videos, mixed),
  // and a SINGLE-media post otherwise. Background music is only offered for a single
  // media; an album never carries music (each video keeps its own sound instead).
  const isAlbum = visualCount > 1;
  const singleMedia = visualCount === 1;
  // « Avant / Après » : exactement 2 médias du même type (2 photos ou 2 vidéos).
  const canBeforeAfter = visualCount === 2 && visualAtts[0].kind === visualAtts[1].kind;
  // A MIXED album (any video present) is capped at MAX_MIXED items total; a pure-photo
  // album keeps the larger MAX_MEDIA cap. Videos are always capped at MAX_VIDEOS.
  const hasVideoAtt = videoAtts.length > 0;
  const visualMax = hasVideoAtt ? MAX_MIXED : MAX_MEDIA;
  const canAddVisual = !audioAtt && visualCount < visualMax;
  const canAddVideo = !audioAtt && videoAtts.length < MAX_VIDEOS && visualCount < MAX_MIXED;
  const mediaAddAccept = canAddVideo ? modalMediaAccept : "image/*";
  // A multi-media album is never a Reel (only a lone short/vertical video is).
  const effectiveIsReel = isReel && !!videoAtt && !isAlbum;
  const hasDraft = !!content.trim() || hasMedia;

  // Music only makes sense over a SINGLE photo/video — drop it for albums or when
  // the visual is removed.
  useEffect(() => {
    if (!singleMedia && (music || keepVideoSound)) { setMusic(null); setKeepVideoSound(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleMedia]);
  useEffect(() => { if (!canBeforeAfter && layout) setLayout(null); }, [canBeforeAfter, layout]);

  const reset = () => {
    attachments.forEach((a) => a.localUrl && URL.revokeObjectURL(a.localUrl));
    setContent(""); setAttachments([]); setMentions([]);
    setIsReel(false); setLayout(null); setBg(null); setShowPalette(false); setTextScale(1); scaleTouched.current = false; setEmojiTray(false);
    setMusic(null); setKeepVideoSound(false); setMusicVolume(0.7);
    setLinkPreview(null);
    setScheduleOn(false); setScheduledAt("");
    setDestinations({ personal: !groupId, groups: [] });
    setAsTestimony(false); setTestimonyCat("other");
    setPublishAs(null);
    dismissedLinkRef.current = null; fetchedLinkRef.current = null;
    reelToastedRef.current = false;
    localStorage.removeItem(draftKey);
  };

  const flagReel = () => {
    setIsReel(true);
    if (!reelToastedRef.current) {
      reelToastedRef.current = true;
      toast.info(t("reel.detected", "🎬 Vidéo courte détectée — elle sera publiée en Reel"));
    }
  };

  const uploadAttachment = async (att) => {
    setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, status: "uploading", progress: 0 } : a)));
    try {
      const data = await uploadMediaFile(att.file, {
        config,
        onProgress: (p) => setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, progress: p } : a))),
      });
      setAttachments((prev) => prev.map((a) => (a.id === att.id
        ? { ...a, url: data.url, status: "done", progress: 100, width: data.width, height: data.height, duration: data.duration || a.duration }
        : a)));
      // Durée inconnue → jamais un Reel (le serveur reclasse dès que le VOD renvoie la vraie durée).
      if (att.kind === "video" && !groupId && data.width && data.height && (reelsHorizontal || data.height >= data.width) && data.duration > 0 && data.duration <= reelMaxSec) {
        flagReel();
      }
      // Generate a sharp poster (first video frame) so Reels/feed show a still before playback.
      if (att.kind === "video" && att.file) {
        generateVideoPosterUrl(att.file)
          .then((purl) => { if (purl) setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, posterUrl: purl } : a))); })
          .catch(() => {});
      }
    } catch (e) {
      setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, status: "error" } : a)));
      toast.error(`Erreur upload ${att.file.name}: ${e?.friendlyMessage || e?.message || "?"}`);
    }
  };

  // Facebook-style: instant local preview, upload runs in the background.
  // `extra` (caméra intégrée) : fx vidéo + durée/dimensions mesurées (un WebM filmé annonce une durée infinie).
  const handleFiles = (files, extra = {}) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    let audio = arr.find((f) => f.type.startsWith("audio"));
    // Visuals kept in the ORDER the OS returns them (photos + videos interleaved).
    let incoming = arr.filter((f) => f.type.startsWith("image") || f.type.startsWith("video"));
    const existingVisual = attachments.some((a) => a.kind === "image" || a.kind === "video");
    // A post is EITHER a standalone-audio post OR a visual album (photos +/or
    // videos) with, when it's a single media, an optional background music.
    if (audio && (incoming.length > 0 || existingVisual)) {
      audio = null;
      toast.info(t("composer.audio_over_media", "Pour une photo ou une vidéo, ajoutez plutôt une musique de fond ci-dessous."));
    }
    // Cap the videos at MAX_VIDEOS first, keeping add order.
    const curCount = attachments.filter((a) => a.kind === "image" || a.kind === "video").length;
    const curVideos = attachments.filter((a) => a.kind === "video").length;
    const vidRoom = Math.max(0, MAX_VIDEOS - curVideos);
    let seenVid = 0;
    let droppedVideo = false;
    incoming = incoming.filter((f) => {
      if (f.type.startsWith("video")) {
        if (seenVid >= vidRoom) { droppedVideo = true; return false; }
        seenVid += 1;
      }
      return true;
    });
    if (droppedVideo) {
      toast.info(t("composer.max_videos", `${MAX_VIDEOS} vidéos maximum par publication.`));
    }
    // A MIXED album (any video present) is capped at MAX_MIXED items total; a
    // pure-photo album keeps the larger MAX_MEDIA cap. Keeps add order.
    const willHaveVideo = (curVideos + incoming.filter((f) => f.type.startsWith("video")).length) > 0;
    const totalCap = willHaveVideo ? MAX_MIXED : MAX_MEDIA;
    const room = Math.max(0, totalCap - curCount);
    if (incoming.length > room) {
      incoming = incoming.slice(0, room);
      toast.info(willHaveVideo
        ? t("composer.mixed_max", `Album avec vidéo : ${MAX_MIXED} éléments maximum.`)
        : t("composer.media_max", `${MAX_MEDIA} médias maximum par publication.`));
    }
    const picked = audio
      ? [{ kind: "audio", file: audio }]
      : incoming.map((f) => ({ kind: f.type.startsWith("video") ? "video" : "image", file: f }));
    if (picked.length === 0) return;
    const camExtra = extra && extra.duration > 0 ? { duration: extra.duration, width: extra.width || 0, height: extra.height || 0 } : {};
    const newAtts = picked.map((p) => ({
      id: newId(), ...p, localUrl: URL.createObjectURL(p.file),
      url: null, progress: 100, status: "done",
      ...(p.kind === "video" ? { ...camExtra, fx: extra?.fx || null } : {}),
    }));
    // Vidéo filmée dans l'app : la durée vient de la caméra (métadonnées du fichier inexploitables) → Reel si courte et verticale.
    if (camExtra.duration && picked[0]?.kind === "video" && !groupId && curCount === 0 && camExtra.height > 0 && (reelsHorizontal || camExtra.height >= camExtra.width) && camExtra.duration <= reelMaxSec) flagReel();
    const revoke = (list) => list.forEach((a) => a.localUrl?.startsWith("blob:") && URL.revokeObjectURL(a.localUrl));
    setAttachments((prev) => {
      let next = [...prev];
      const drop = (kinds) => { revoke(next.filter((a) => kinds.includes(a.kind))); next = next.filter((a) => !kinds.includes(a.kind)); };
      if (audio) {
        // Standalone audio post: it replaces any visual album.
        drop(["image", "audio", "video"]);
        setIsReel(false); reelToastedRef.current = false;
      } else if (next.some((a) => a.kind === "audio")) {
        // Adding a visual clears a previous standalone-audio selection.
        drop(["audio"]);
      }
      next.push(...newAtts);
      return next;
    });
    setBg(null); setShowPalette(false);
    setOpen(true);
    // Media is now uploaded in the BACKGROUND when the user hits "Publier"
    // (Facebook-style), so we only build the local preview here — no upload yet.
    // Instant local poster for each video preview — WebView Android does not paint
    // the first frame of a <video> on its own, so we capture it and use it as `poster`.
    newAtts.filter((a) => a.kind === "video" && a.file).forEach((va) => {
      captureVideoPoster(va.file)
        .then((blob) => {
          if (!blob) return;
          const purl = URL.createObjectURL(blob);
          setAttachments((prev) => prev.map((a) => (a.id === va.id ? { ...a, localPoster: purl } : a)));
        })
        .catch(() => {});
    });
    // Capture the audio duration locally so it's persisted with the post (shown
    // everywhere — player, share card — without re-downloading the file).
    const audioAttNew = newAtts.find((a) => a.kind === "audio");
    if (audioAttNew) {
      const el = document.createElement("audio");
      el.preload = "metadata";
      // Blob audio often reports duration=Infinity on loadedmetadata; the real
      // value arrives on durationchange — listen to both and take the finite one.
      const onDur = () => {
        if (isFinite(el.duration) && el.duration > 0) {
          el.removeEventListener("loadedmetadata", onDur);
          el.removeEventListener("durationchange", onDur);
          setAttachments((prev) => prev.map((a) => (a.id === audioAttNew.id ? { ...a, duration: el.duration } : a)));
        }
      };
      el.addEventListener("loadedmetadata", onDur);
      el.addEventListener("durationchange", onDur);
      el.src = audioAttNew.localUrl;
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.localUrl && att.localUrl.startsWith("blob:")) URL.revokeObjectURL(att.localUrl);
      if (att?.localPoster) URL.revokeObjectURL(att.localPoster);
      if (att?.kind === "video") { setIsReel(false); reelToastedRef.current = false; }
      return prev.filter((a) => a.id !== id);
    });
  };

  // Drag-and-drop reorder of the album's visuals (photos + videos). An album never
  // carries audio, so we simply persist the new visual order back into attachments.
  const reorderVisuals = (next) => {
    setAttachments((prev) => {
      const nonVisual = prev.filter((a) => a.kind !== "image" && a.kind !== "video");
      return [...next, ...nonVisual];
    });
  };

  // Per-media caption (shown under each photo/video of the album, Facebook-style).
  const setCaption = (id, text) => setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, caption: text } : a)));

  // Lot 534 — éditeur Filtres / Réglages / Recadrer sur un média du composeur : photo → fichier « cuit » qui remplace
  // l'original (envoyé tel quel) ; vidéo → `fx` conservé sur la pièce jointe (post.video_fx / album[i].fx, CSS à la lecture).
  const { openFx, fxProps } = useFxEditor();
  const editFx = async (a) => {
    const res = await openFx({ kind: a.kind, src: a.localUrl || resolveMediaUrl(a.url), file: a.file || null, poster: a.localPoster || (a.posterUrl ? resolveMediaUrl(a.posterUrl) : null) }, a.fx);
    if (!res) return;
    if (res.file) {
      setAttachments((prev) => prev.map((x) => {
        if (x.id !== a.id) return x;
        if (x.localUrl?.startsWith("blob:")) URL.revokeObjectURL(x.localUrl);
        return { ...x, file: res.file, localUrl: URL.createObjectURL(res.file), url: null, width: 0, height: 0, progress: 100, status: "done", fx: null };
      }));
    } else if ("fx" in res) {
      setAttachments((prev) => prev.map((x) => (x.id === a.id ? { ...x, fx: res.fx } : x)));
    }
  };

  // Capture a first-frame thumbnail for a native-picked video (Capacitor file URL) so
  // the composer preview + feed show a real still immediately — no grey WebView box.
  const captureNativePoster = async (attId, previewUrl) => {
    if (!previewUrl) return;
    try {
      const blob = await captureVideoPosterFromUrl(previewUrl);
      if (!blob) return;
      const purl = URL.createObjectURL(blob);
      setAttachments((prev) => prev.map((a) => (a.id === attId ? { ...a, localPoster: purl, posterBlob: blob } : a)));
    } catch { /* server generates it as a fallback */ }
  };

  // Android: pick a feed video with the native picker (returns a filesystem path — no
  // in-memory read, so no OOM). It uploads in the background when the user publishes.
  const addNativeVideo = async () => {
    if (!canAddVideo) { toast.info(t("composer.mixed_max", `Album avec vidéo : ${MAX_MIXED} éléments maximum.`)); return; }
    const d = await pickVideoNative();
    if (!d) return;
    const vidId = newId();
    setAttachments((prev) => {
      // Album-style: append the video, dropping only a standalone-audio selection.
      prev.filter((a) => a.kind === "audio" && a.localUrl?.startsWith("blob:")).forEach((a) => URL.revokeObjectURL(a.localUrl));
      const next = prev.filter((a) => a.kind !== "audio");
      next.push({
        id: vidId, kind: "video", file: null, nativeVideo: d,
        localUrl: d.previewUrl, localPoster: null, url: null, progress: 100, status: "done",
        width: d.width, height: d.height, duration: d.duration,
      });
      return next;
    });
    captureNativePoster(vidId, d.previewUrl);
    setBg(null); setShowPalette(false); setOpen(true);
    if (!groupId && attachments.filter((a) => a.kind === "image" || a.kind === "video").length === 0 && d.width && d.height && (reelsHorizontal || d.height >= d.width) && d.duration > 0 && d.duration <= reelMaxSec) flagReel();
    else { setIsReel(false); reelToastedRef.current = false; }
  };

  // Android/iOS: a SINGLE media button — native combined picker. Several PHOTOS at once (up to
  // the album room) OR one video: videos keep the native background upload (no OOM, keeps going
  // when backgrounded); photos become Files and go through the SAME handleFiles pipeline as the web.
  const addNativeMedia = async () => {
    const room = Math.max(1, visualMax - visualCount);
    const picked = await pickMediaNative(room);
    if (picked.length === 0) return;
    const videos = picked.filter((x) => x.kind === "video");
    const images = picked.filter((x) => x.kind === "image");
    if (videos.length > 0 && images.length > 0) {
      toast.info(t("composer.native_pick_mixed", "Plusieurs photos OU une vidéo par sélection : la vidéo n'a pas été ajoutée, ajoute-la ensuite."));
    } else if (videos.length > 1) {
      toast.info(t("composer.native_pick_one_video", "Une seule vidéo par sélection : la première a été ajoutée."));
    }
    if (images.length === 0) {
      const m = videos[0];
      if (!canAddVideo) { toast.info(t("composer.mixed_max", `Album avec vidéo : ${MAX_MIXED} éléments maximum.`)); return; }
      const vidId = newId();
      setAttachments((prev) => {
        // Album-style: append the video, dropping only a standalone-audio selection.
        prev.filter((a) => a.kind === "audio" && a.localUrl?.startsWith("blob:")).forEach((a) => URL.revokeObjectURL(a.localUrl));
        const next = prev.filter((a) => a.kind !== "audio");
        next.push({
          id: vidId, kind: "video", file: null, nativeVideo: m,
          localUrl: m.previewUrl, localPoster: null, url: null, progress: 100, status: "done",
          width: m.width, height: m.height, duration: m.duration,
        });
        return next;
      });
      captureNativePoster(vidId, m.previewUrl);
      setBg(null); setShowPalette(false); setOpen(true);
      if (!groupId && attachments.filter((a) => a.kind === "image" || a.kind === "video").length === 0 && m.width && m.height && (reelsHorizontal || m.height >= m.width) && m.duration > 0 && m.duration <= reelMaxSec) flagReel();
      else { setIsReel(false); reelToastedRef.current = false; }
      return;
    }
    // Photos: native paths → Files (selection order kept) → standard image pipeline, in ONE call so caps apply once.
    try {
      const files = await Promise.all(images.map(async (m) => {
        const blob = await (await fetch(m.previewUrl)).blob();
        return new File([blob], m.name || "image.jpg", { type: m.mimeType || blob.type || "image/jpeg" });
      }));
      handleFiles(files);
    } catch {
      toast.error(t("composer.media_error", "Impossible d'ajouter ce média, réessaie."));
    }
  };

  // Authoritative silent detection from the local preview element metadata.
  const onVideoMeta = (e) => {
    const v = e.currentTarget;
    if (!v.videoWidth || groupId) return;
    if (!isFinite(v.duration)) return; // durée inconnue (WebM filmé dans l'app) : on garde la décision prise à l'ajout
    const vertical = v.videoHeight >= v.videoWidth;
    const shortEnough = isFinite(v.duration) && v.duration > 0 && v.duration <= reelMaxSec;
    if (shortEnough && (reelsHorizontal || vertical)) flagReel();
    else { setIsReel(false); }
  };

  const addEmoji = (e) => { setContent((c) => c + e); setOpen(true); };

  const closeDialog = (o) => {
    if (posting) return;
    setOpen(o);
    if (!o) {
      setEmojiTray(false);
      dismissedLinkRef.current = null;
      if (hasDraft) toast.info(t("composer.draft_kept", "Brouillon conservé — vous pourrez reprendre plus tard"), { duration: 2500 });
    }
  };
  // Abandonner : vide le brouillon puis ferme (sans le toast « Brouillon conservé »).
  const discardAndClose = () => { if (posting) return; reset(); setEmojiTray(false); dismissedLinkRef.current = null; setOpen(false); };

  const submitInline = async ({ church_id, scheduled }) => {
    setPosting(true);
    try {
      // Upload any locally-selected media now (no-media posts skip this loop).
      const uploaded = [];
      for (const a of attachments) {
        if (a.url) { uploaded.push(a); continue; }
        let data, posterUrl = null;
        if (a.nativeVideo) {
          data = await uploadVideoNativeBackground({ ...a.nativeVideo, context: "feed", isReel, compress: config?.settings?.features?.video_compression !== false, config });
          // Thumbnail captured at pick time (instant), else capture now from the local
          // file URL — so the post has a real poster immediately (no WebView placeholder).
          if (a.posterBlob) posterUrl = await uploadPosterBlob(a.posterBlob).catch(() => null);
          if (!posterUrl && a.nativeVideo.previewUrl) {
            const b = await captureVideoPosterFromUrl(a.nativeVideo.previewUrl).catch(() => null);
            if (b) posterUrl = await uploadPosterBlob(b).catch(() => null);
          }
        } else {
          data = await uploadMediaFile(a.file, { config });
          if (a.kind === "video") posterUrl = await generateVideoPosterUrl(a.file).catch(() => null);
        }
        uploaded.push({ ...a, url: data.url, posterUrl });
      }
      const visuals = uploaded.filter((a) => a.kind === "image" || a.kind === "video");
      const imgs = visuals.filter((a) => a.kind === "image").map((a) => a.url);
      // Dimensions des photos → le fil réserve la place exacte (zéro saut d'image au chargement).
      const imageDims = Object.fromEntries(visuals.filter((a) => a.kind === "image" && a.width > 0 && a.height > 0).map((a) => [a.url, [a.width, a.height]]));
      if (Object.keys(imageDims).length) payload.image_dims = imageDims;
      const vid = visuals.find((a) => a.kind === "video");
      const aud = uploaded.find((a) => a.kind === "audio");
      let audDur = (aud && isFinite(aud.duration) && aud.duration > 0) ? aud.duration : null;
      if (aud && !audDur) {
        audDur = await readAudioDuration(aud.localUrl || (aud.url ? resolveMediaUrl(aud.url) : null));
      }
      const dest = resolveDest();
      const payload = {
        content: !hasMedia && bg ? stripRichMarkers(content) : content,
        images: imgs,
        group_id: dest.group_id,
        ...(dest.group_ids?.length ? { group_ids: dest.group_ids } : {}),
        audio_url: aud?.url || null,
        audio_duration: audDur ? Math.round(audDur * 100) / 100 : null,
        bg: hasMedia ? null : bg,
        text_scale: !hasMedia && bg && textScale !== 1 ? textScale : null,
      };
      if (usedMentions.length) payload.mentions = usedMentions;
      if (vid?.url) { payload.video_url = vid.url; payload.is_reel = effectiveIsReel; if (vid.posterUrl) payload.poster_url = vid.posterUrl; if (vid.duration && isFinite(vid.duration) && vid.duration > 0) payload.video_duration = Math.round(vid.duration * 100) / 100; }
      // Ordered mixed-media album (photos + videos) — preserves the user's add order.
      if (visuals.length > 1) {
        payload.album = visuals.map((a) => ({
          type: a.kind, url: a.url,
          ...(a.width > 0 && a.height > 0 ? { width: a.width, height: a.height } : {}),
          ...(a.posterUrl ? { poster_url: a.posterUrl } : {}),
          ...(a.duration && isFinite(a.duration) && a.duration > 0 ? { duration: Math.round(a.duration * 100) / 100 } : {}),
          ...(a.caption && a.caption.trim() ? { caption: a.caption.trim() } : {}),
        }));
      }
      if (layout === "before_after" && visuals.length === 2 && visuals[0].kind === visuals[1].kind) payload.album_layout = layout;
      if (music?.url && singleMedia) {
        payload.music_url = music.url;
        payload.music_title = music.title || null;
        if (vid) payload.music_keep_media_sound = keepVideoSound;
        payload.music_volume = musicVolume;
        if (music.start) payload.music_start = music.start;
      }
      if (church_id) payload.church_id = church_id;
      if (!church_id && !dest.group_id) payload.visibility = audience;
      if (testimonyOn) { payload.post_type = "testimony"; payload.testimony_category = testimonyCat; }
      if (linkPreview && !hasMedia) payload.embed = linkPreview;
      else if (!hasMedia && dismissedLinkRef.current && parseFirstUrl(content)) payload.embed_dismissed = true;
      if (scheduled) { try { payload.scheduled_at = new Date(scheduledAt).toISOString(); } catch { /* noop */ } }
      const { data } = await api.post("/posts", payload);
      const wasReel = effectiveIsReel && !!vid;
      const wasScheduled = !!data.scheduled;
      reset(); setOpen(false);
      if (wasScheduled) {
        window.dispatchEvent(new Event("yalola:post_scheduled"));
        toast.success(t("post.scheduled_ok", "Publication programmée ✓ — elle sera publiée automatiquement."));
      } else if (data.moderation?.message) {
        // Media flagged at upload time (e.g. revealing outfit): clear, apologetic notice.
        toast.warning(data.moderation.title || t("post.moderation_pending", "Publication en attente de vérification"), {
          description: data.moderation.message, duration: 15000,
        });
      } else {
        const nGroups = (data.group_copies?.length || 0) + (data.post?.group_id ? 1 : 0);
        toast.success(wasReel ? t("reel.published", "Reel publié ! 🎬") : nGroups > 1 ? t("post.published_in_groups", "Publié dans {n} groupes").replace("{n}", String(nGroups)) : t("post.published", "Publication envoyée !"));
        flagBioNudge(user);
        if (wasReel) nav("/reels");
        else onPublished?.(data.post);
      }
    } catch (e) {
      if (e?.code === "SOURCE_UNREADABLE") {
        // Lot 736 — accès à la vidéo choisie expiré (sélecteur photo Android) : on la retire et on demande de la re-choisir.
        setAttachments((prev) => prev.filter((a) => !a.nativeVideo));
        toast.error(t("composer.video_access_lost", "L'accès à la vidéo a expiré — choisis-la à nouveau."));
      } else {
        toast.error(t("common.error", "Erreur") + " : " + (e?.friendlyMessage || e?.message));
      }
    } finally {
      setPosting(false);
    }
  };

  // Facebook-style publish: media posts upload in the BACKGROUND (queue) so the
  // composer closes immediately — programmées comprises (une vidéo de 60 Mo ne fige plus le bouton).
  // Text-only posts stay inline.
  const doPublish = () => {
    if (!content.trim() && !hasMedia) {
      toast.info(t("post.empty", "Écris quelque chose ou ajoute un média."));
      return;
    }
    if (needsPublishAs && !publishAs) return;
    let church_id = churchId || null;
    if (!church_id && needsPublishAs && publishAs && publishAs !== "me") church_id = publishAs;
    if (needsPublishAs && rememberDest && publishAs && memKey) { try { localStorage.setItem(memKey, publishAs); } catch { /* noop */ } }
    const scheduled = scheduleOn && !!scheduledAt;
    if (!hasMedia) { submitInline({ church_id, scheduled }); return; }
    let scheduledIso = null;
    if (scheduled) {
      try { scheduledIso = new Date(scheduledAt).toISOString(); } catch { /* noop */ }
      if (!scheduledIso || new Date(scheduledIso).getTime() <= Date.now() + 60000) {
        toast.error(t("schedule.past", "Choisis une date et une heure futures."));
        return;
      }
    }
    // Preserve the user's add order (photos + videos interleaved) for the album.
    const queued = attachments
      .filter((a) => a.kind === "audio" ? (!hasVisual) : true)
      .map((a) => ({
        kind: a.kind, file: a.file, url: a.url, width: a.width, height: a.height,
        ...(a.caption && a.caption.trim() ? { caption: a.caption.trim() } : {}),
        ...(a.kind === "video" ? { nativeVideo: a.nativeVideo || null, duration: a.duration, fx: a.fx || null } : {}),
        ...(a.kind === "audio" ? { duration: a.duration } : {}),
      }));
    const previewFile = visualAtts[0]?.file || audioAtt?.file || null;
    // Vidéo native : on transmet l'image fixe capturée (Blob JPEG), JAMAIS l'URL du fichier vidéo (un <img> dessus = « image cassée »).
    const nativePosterBlob = videoAtt?.posterBlob || null;
    const mediaKind = isAlbum ? "album" : imageAtts.length ? "image" : videoAtt ? "video" : audioAtt ? "audio" : "text";
    const musicInput = (music?.url && singleMedia)
      ? { url: music.url, title: music.title || null, keepVideoSound: !!(videoAtt && keepVideoSound), volume: musicVolume, start: music.start || 0 }
      : null;
    const dest = resolveDest();
    enqueuePost({ content, attachments: queued, isReel: effectiveIsReel, groupId: dest.group_id, groupIds: dest.group_ids || null, church_id, visibility: (!church_id && !dest.group_id) ? audience : null, previewFile, nativePosterBlob, mediaKind, music: musicInput, mentions: usedMentions, scheduled_at: scheduledIso, album_layout: canBeforeAfter ? layout : null, ...(testimonyOn ? { post_type: "testimony", testimony_category: testimonyCat } : {}) });
    if (scheduledIso) toast.info(t("post.schedule_uploading", "Envoi du média en cours — la publication sera programmée dès qu'il est prêt."), { duration: 4000 });
    else flagBioNudge(user);
    reset(); setOpen(false);
  };

  if (!user) return null;

  // Lot 779 — aperçu « telle qu'au fil » : carte construite à l'ouverture (instantané), « Publier » depuis l'aperçu.
  const canPublish = !posting && (!!content.trim() || hasMedia) && !(needsPublishAs && !publishAs);
  const openPreview = () => { if (!content.trim() && !hasMedia) { toast.info(t("post.empty", "Écris quelque chose ou ajoute un média.")); return; } setPreviewPost(buildPreviewPost(fsState, user)); };
  const publishFromPreview = () => { setPreviewPost(null); if (needsPublishAs && !publishAs) { toast.info(t("composer.publish_as_required", "Choisissez une destination pour publier.")); return; } doPublish(); };
  const publishLabel = (scheduleOn && scheduledAt) ? t("post.schedule_btn", "Programmer") : t("post.publish", "Publier");

  // Tout l'état/les actions du composeur, pour l'habillage plein écran téléphone.
  const fsState = {
    content, setContent, attachments, bg, pickBg, bgMax, bgTooLong, textScale, setTextScale, scaleTouched,
    setBgCanvas, bgTextareaRef, plainTextareaRef, placeholder, native, handleFiles, addNativeMedia, addNativeVideo,
    removeAttachment, reorderVisuals, setCaption, uploadAttachment, editFx, imageAtts, videoAtt, audioAtt, visualAtts,
    hasMedia, hasVisual, isAlbum, singleMedia, layout, setLayout, canBeforeAfter, canAddVisual, canAddVideo, mediaAddAccept, effectiveIsReel, onVideoMeta, previewVideoRef,
    music, setMusic, musicVolume, setMusicVolume, keepVideoSound, setKeepVideoSound, linkPreview, removeLinkPreview,
    scheduleOn, setScheduleOn, scheduledAt, setScheduledAt, needsPublishAs, publishAs, setPublishAs, myChurches, churchId, churchName, groupId,
    audience, setAudience, audienceEnabled, usedMentions, testimonyOn, testimonyCat,
    multiGroupsOn, destLabel, destCount, openGroupsSheet: () => setGroupsSheet(true),
    insertEmoji, doPublish, posting, hasDraft, closeDialog, discardAndClose, mentionAc, startMention, startHashtag,
    cameraOpen, setCameraOpen, cameraMode, openCamera, onCameraCapture, cameraMaxSec: Math.min(60, reelMaxSec),
    previewOn, previewPost, openPreview, closePreview: () => setPreviewPost(null), publishFromPreview, publishLabel,
  };

  // Modal PC (web) avec photo/vidéo : aperçu épinglé à gauche, texte + options à droite (même schéma que la story) —
  // plus de défilement pour retrouver la musique, « Ajouter à votre publication » ou la programmation. Natif : inchangé.
  const twoCols = hasVisual && !native;
  const mediaPreviews = isAlbum
    ? <AlbumPreviews visualAtts={visualAtts} reorderVisuals={reorderVisuals} removeAttachment={removeAttachment} setCaption={setCaption} uploadAttachment={uploadAttachment} onEditFx={editFx} layout={layout} setLayout={setLayout} canBeforeAfter={canBeforeAfter} t={t} />
    : <SinglePreview imageAtts={imageAtts} videoAtt={videoAtt} effectiveIsReel={effectiveIsReel} onVideoMeta={onVideoMeta} previewVideoRef={previewVideoRef} removeAttachment={removeAttachment} uploadAttachment={uploadAttachment} onEditFx={editFx} t={t} />;

  return (
    <div className={variant === "button" ? "inline-flex" : "yalola-card p-3"} data-testid={variant === "button" ? "church-composer-block" : "create-post-block"}>
      {/* Collapsed row (card) or single pill button (church page) */}
      {variant === "button" ? (
        <Button className="rounded-full pill-primary" onClick={() => setOpen(true)} data-testid="church-publish-post-btn">
          <Pencil size={15} className="mr-1" /> {t("church.publish_post", "Publier")}
        </Button>
      ) : (
      <div data-testid="composer-card" className="relative">
        {gated && (
          <button type="button" onClick={() => requireCompleteProfile(user)} className="absolute inset-0 z-10 rounded-xl cursor-pointer" style={{ background: "transparent" }}
            aria-label={t("profile_done.gate_cta", "Complète ton profil pour publier")} data-testid="composer-gate" />
        )}
        {/* Lot 583 — façon Facebook : ligne 1 = vignette + « Quoi de neuf… » en pleine largeur ; ligne 2 = les 4 actions. */}
        <div className="flex items-center gap-2.5">
          <Avatar className="h-10 w-10 shrink-0" style={{ boxShadow: "0 0 0 2px var(--yalola-primary-soft)" }}>
            <AvatarImage src={avatarSrc(user)} />
            <AvatarFallback>{user.name?.[0]}</AvatarFallback>
          </Avatar>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex-1 min-w-0 text-left rounded-full px-4 py-2.5 text-sm truncate transition-colors hover:brightness-[0.98]"
            style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}
            data-testid="composer-open"
          >
            {content.trim() || placeholder}
          </button>
          {hasDraft && (
            <span
              className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0"
              style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}
              data-testid="composer-draft-chip"
            >
              {t("composer.draft", "Brouillon")}
            </span>
          )}
        </div>
        <div className="flex items-center mt-3 pt-2.5 border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid="composer-actions">
          {phoneFs && (
            <button type="button" onClick={() => openCamera("photo")} title={t("camera.open", "Caméra")} data-testid="composer-camera-btn" className={COMPOSER_ACTION_CLS}>
              <Camera size={21} className="text-sky-600 shrink-0" /><span>{t("composer.act_camera", "Caméra")}</span>
            </button>
          )}
          <button type="button" onClick={() => nav("/live?create=1")} title="Live" data-testid="composer-live-btn" className={COMPOSER_ACTION_CLS}>
            <Video size={21} className="text-rose-600 shrink-0" /><span>{t("composer.act_live", "Live")}</span>
          </button>
          {native ? (
            <button type="button" onClick={addNativeMedia} title={t("composer.media", "Photo ou vidéo")} data-testid="composer-media" className={COMPOSER_ACTION_CLS}>
              <ImageIcon size={21} className="text-emerald-600 shrink-0" /><span>{t("composer.act_photo", "Photo")}</span>
            </button>
          ) : (
            <FileUpload
              accept={mediaAccept}
              multiple
              onFiles={handleFiles}
              variant="custom"
              testId="composer-media"
              className={`${COMPOSER_ACTION_CLS} relative`}
            >
              <ImageIcon size={21} className="text-emerald-600 shrink-0" /><span>{t("composer.act_photo", "Photo")}</span>
            </FileUpload>
          )}
          <EmojiPicker onEmoji={addEmoji} label={t("composer.act_mood", "Humeur")} className={COMPOSER_ACTION_CLS} />
          {config?.settings?.composer_prayer_action !== false && (
            <button type="button" onClick={() => nav("/prayers?create=1")} title={t("prayers.create", "Demander une prière")} data-testid="composer-prayer-btn" className={COMPOSER_ACTION_CLS}>
              <HandHeart size={21} className="text-violet-600 shrink-0" /><span>{t("composer.act_prayer", "Prière")}</span>
            </button>
          )}
        </div>
      </div>
      )}

      {/* Téléphone / app native : composeur plein écran façon Instagram */}
      {phoneFs && open && <PostFullscreenComposer c={fsState} />}
      {phoneFs && <MediaFxEditor {...fxProps} t={t} />}

      {/* Facebook-style "Créer une publication" modal (PC) */}
      <Dialog open={open && !phoneFs} onOpenChange={closeDialog}>
        <DialogContent variant="sheet" className={twoCols ? PC_COMPOSER_MODAL : undefined} data-testid="composer-modal" onInteractOutside={keepDialogOpenForFx}>
          <MediaFxEditor {...fxProps} t={t} />
          <DialogHeader>
            <DialogTitle className="text-center font-display">{t("post.create_title", "Créer une publication")}</DialogTitle>
            <DialogDescription className="sr-only">{placeholder}</DialogDescription>
          </DialogHeader>

          <DialogBody className="pt-3">
           <div className={twoCols ? "md:grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] md:gap-5 md:items-start" : ""} data-testid="composer-layout" data-columns={twoCols ? "2" : "1"}>
            {twoCols && (
              <div className="md:sticky md:top-0 md:self-start" data-testid="composer-media-column">
                {mediaPreviews}
              </div>
            )}
            <div className="min-w-0" data-testid="composer-form-column">
            <div className="flex items-center gap-2.5 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={avatarSrc(user)} />
                <AvatarFallback>{user.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-semibold">{user.name}</div>
                {churchId && (
                  <span className="chip text-[11px] inline-flex items-center gap-1 mt-0.5" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid="composer-church-target">
                    <Church size={12} /> {t("composer.publish_to_church", "Publiera sur la page de {name}").replace("{name}", churchName)}
                  </span>
                )}
              </div>
            </div>

            {/* Preacher: mandatory "Publish as" destination (Me / one of their churches) */}
            {needsPublishAs && (
              <div className="mb-3" data-testid="composer-publish-as">
                <div className="text-xs font-semibold mb-1.5" style={{ color: "var(--yalola-text-muted)" }}>
                  {t("composer.publish_as", "Publier en tant que")}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPublishAs("me")}
                    aria-pressed={publishAs === "me"}
                    className="chip transition-colors"
                    style={publishAs === "me"
                      ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "transparent" }
                      : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
                    data-testid="composer-publish-as-me"
                  >
                    {t("composer.publish_as_me", "Moi (Compte personnel)")}
                  </button>
                  {myChurches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setPublishAs(c.id)}
                      aria-pressed={publishAs === c.id}
                      className="chip transition-colors flex items-center gap-1"
                      style={publishAs === c.id
                        ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "transparent" }
                        : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
                      data-testid={`composer-publish-as-${c.id}`}
                    >
                      <Church size={13} /> {c.name}
                    </button>
                  ))}
                </div>
                {!publishAs && (
                  <div className="text-[11px] mt-1.5" style={{ color: "var(--yalola-primary)" }} data-testid="composer-publish-as-hint">
                    {t("composer.publish_as_required", "Choisissez une destination pour publier.")}
                  </div>
                )}
              </div>
            )}

            {/* Zone de texte — devient un cadre coloré/photo quand un fond est choisi.
                Cadre partagé avec les cartes publiées (BgTextFrame, ratio 4:3) : le texte se dimensionne À L'INTÉRIEUR,
                la carte ne bouge donc jamais avec le curseur de taille, et l'aperçu = la carte du fil. */}
            {bg && !hasMedia ? (
              <BgTextFrame ref={setBgCanvas} bg={bg} className={`grid place-items-center ${BG_TEXT_PAD}`} data-testid="composer-bg-canvas">
                <Textarea
                  ref={bgTextareaRef}
                  autoFocus
                  placeholder={placeholder}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="relative resize-none overflow-hidden border-0 focus-visible:ring-0 bg-transparent p-0 text-center font-bold text-white placeholder:text-white/70 min-h-0 shadow-none"
                  style={{ ...BG_TEXT_SHADOW, lineHeight: 1.3 }}
                  rows={1}
                  maxLength={bgMax}
                  data-testid="composer-textarea"
                />
                <span
                  className="absolute bottom-2 right-3 text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md"
                  style={{ background: "rgba(0,0,0,0.35)", color: content.length >= bgMax ? "#fca5a5" : "rgba(255,255,255,0.85)" }}
                  aria-live="polite"
                  data-testid="composer-bg-counter"
                  data-limit={content.length >= bgMax ? "1" : "0"}
                >
                  {content.length}/{bgMax}
                </span>
              </BgTextFrame>
            ) : (
              <PostRichEditor
                editorRef={plainTextareaRef}
                autoFocus
                placeholder={placeholder}
                value={content}
                onChange={setContent}
                className="text-base min-h-[5rem]"
                toolbarClassName="mb-1.5 w-max"
                testId="composer-textarea"
                toolbarTestId="composer-rich-toolbar"
              />
            )}
            {/* @mention : membres proposés sous le champ (PC + feuille) */}
            <MentionSuggestions ac={mentionAc} className="mt-1" testId="composer-mention-suggestions" t={t} />

            {/* Background palette (text-only posts, like Facebook) */}
            {!hasMedia && (
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShowPalette((v) => !v)}
                  disabled={bgTooLong}
                  className="h-8 w-8 rounded-lg grid place-items-center text-white text-xs font-bold shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "linear-gradient(135deg,#7B2FF7,#F107A3,#F5AF19)" }}
                  title={t("composer.colored_bg", "Fond coloré")}
                  data-testid="composer-bg-toggle"
                >
                  Aa
                </button>
                {bgTooLong && (
                  <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-bg-too-long">
                    {t("composer.bg_text_too_long", "Texte trop long pour un fond coloré ({n} caractères max.)").replace("{n}", bgMax)}
                  </span>
                )}
                {showPalette && !bgTooLong && (
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid="composer-bg-palette">
                    <button
                      type="button"
                      onClick={() => pickBg(null)}
                      className="h-7 w-7 rounded-full border-2 grid place-items-center"
                      style={{ borderColor: !bg ? "var(--yalola-primary)" : "var(--yalola-border)" }}
                      data-testid="composer-bg-none"
                    >
                      <Ban size={13} style={{ color: "var(--yalola-text-muted)" }} />
                    </button>
                    {BG_GRADIENTS.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => pickBg(g.key)}
                        className="h-7 w-7 rounded-full transition-transform hover:scale-110"
                        style={{ background: g.css, boxShadow: bg === g.key ? "0 0 0 2px white, 0 0 0 4px var(--yalola-primary)" : "none" }}
                        data-testid={`composer-bg-${g.key}`}
                      />
                    ))}
                    {BG_IMAGES.map((b) => (
                      <button
                        key={b.key}
                        type="button"
                        onClick={() => pickBg(b.key)}
                        title={b.label}
                        className="h-7 w-7 rounded-full transition-transform hover:scale-110 bg-cover bg-center"
                        style={{ backgroundImage: `url(${b.url})`, boxShadow: bg === b.key ? "0 0 0 2px white, 0 0 0 4px var(--yalola-primary)" : "none" }}
                        data-testid={`composer-bg-${b.key.replace("img:", "img-")}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {bg && !hasMedia && (
              <div className="flex items-center gap-3 mt-2" data-testid="composer-text-size">
                <span className="text-xs font-bold text-white h-6 w-6 rounded-md grid place-items-center shrink-0" style={{ background: "var(--yalola-primary)" }} aria-hidden>Aa</span>
                <Slider value={[textScale]} min={0.6} max={1.4} step={0.05} onValueChange={([v]) => { scaleTouched.current = true; setTextScale(v); }} className="flex-1" aria-label={t("composer.text_size", "Taille du texte")} data-testid="composer-text-size-slider" />
                <span className="text-[11px] tabular-nums w-9 text-right" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-text-size-value">{Math.round(textScale * 100)}%</span>
              </div>
            )}

            {/* Media previews — instant local previews (Facebook-style). Album (2+)
                shows an ordered grid; a single media keeps the large preview. PC : dans la colonne de gauche. */}
            {!twoCols && mediaPreviews}

            {audioAtt && <AudioPreview audioAtt={audioAtt} removeAttachment={removeAttachment} uploadAttachment={uploadAttachment} t={t} />}

            {/* Story-style background music over a SINGLE photo/video (never for albums) */}
            {singleMedia && (
              <MusicSection music={music} setMusic={setMusic} musicVolume={musicVolume} setMusicVolume={setMusicVolume} keepVideoSound={keepVideoSound} setKeepVideoSound={setKeepVideoSound} videoAtt={videoAtt} previewVideoRef={previewVideoRef} t={t} testId="composer-music" />
            )}

            {/* Link preview (YouTube / Facebook) — Facebook-style card with LIVE detection */}
            {linkPreview && !hasMedia && <ComposerLinkPreview linkPreview={linkPreview} onRemove={removeLinkPreview} t={t} />}

            {/* Add to your post */}
            <div className="mt-3 rounded-xl border px-3 py-2 flex flex-wrap items-center justify-between gap-y-1" style={{ borderColor: "var(--yalola-border)" }}>
              <span className="text-sm font-semibold whitespace-nowrap">{t("post.add_to", "Ajouter à votre publication")}</span>
              <div className="flex items-center gap-0.5">
                {canAddVisual && (
                  <FileUpload
                    accept={mediaAddAccept}
                    multiple
                    onFiles={handleFiles}
                    variant="custom"
                    testId="composer-modal-media"
                    className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors relative"
                  >
                    <ImageIcon size={21} className="text-emerald-600" />
                  </FileUpload>
                )}
                {native && canAddVideo && (
                  <button type="button" onClick={addNativeVideo} title={t("composer.video", "Vidéo")} data-testid="composer-modal-native-video-btn" className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors">
                    <Film size={21} className="text-indigo-600" />
                  </button>
                )}
                <FileUpload
                  accept="audio/*"
                  onFiles={handleFiles}
                  variant="custom"
                  testId="composer-modal-audio"
                  disabled={hasVisual}
                  className={`h-9 w-9 grid place-items-center rounded-full transition-colors relative ${hasVisual ? "opacity-40 cursor-not-allowed" : "hover:bg-[color:var(--yalola-surface-alt)]"}`}
                >
                  <Music size={20} className="text-purple-600" />
                </FileUpload>
                {!hasMedia && (
                  <button type="button" onClick={() => nav("/live?create=1")} title="Live" data-testid="composer-modal-live" className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors">
                    <Video size={21} className="text-rose-600" />
                  </button>
                )}
                <button type="button" onClick={() => startMention("@")} title={t("composer.mention_hint", "Tape @ puis les premières lettres du nom pour taguer un membre")} aria-label={t("composer.mention_chip", "Mention")} data-testid="composer-modal-mention-btn" className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors">
                  <AtSign size={20} className="text-sky-600" />
                </button>
                <button type="button" onClick={startHashtag} title={t("composer.hashtag_hint", "Tape # puis un mot pour ajouter un hashtag (ex. #tendance)")} aria-label={t("composer.hashtag_chip", "Hashtag")} data-testid="composer-modal-hashtag-btn" className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors">
                  <Hash size={20} className="text-teal-600" />
                </button>
                {isMobileSheet ? (
                  <button
                    type="button"
                    title="Emoji"
                    aria-pressed={emojiTray}
                    onClick={() => { setEmojiTray((v) => !v); if (!emojiTray) revealTextarea(); }}
                    className={`h-9 w-9 grid place-items-center rounded-full transition-colors ${emojiTray ? "bg-[color:var(--yalola-surface-alt)]" : "hover:bg-[color:var(--yalola-surface-alt)]"}`}
                    data-testid="composer-modal-emoji-btn"
                  >
                    <Smile size={21} className="text-amber-500" />
                  </button>
                ) : (
                  <EmojiPicker onEmoji={insertEmoji} testId="composer-modal-emoji-btn" />
                )}
              </div>
            </div>

            {/* Publish as a testimony (also listed in Témoignages) */}
            {testimonyAllowed && (
              <div className="mt-3 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid="composer-testimony">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold flex items-center gap-1.5"><HeartHandshake size={16} style={{ color: "var(--yalola-secondary)" }} /> {t("composer.testimony", "Témoignage")}</span>
                  <Switch checked={asTestimony} onCheckedChange={setAsTestimony} data-testid="composer-testimony-switch" />
                </div>
                {asTestimony && (
                  <div className="mt-2 space-y-2">
                    <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("composer.testimony_hint", "Publier comme témoignage : ta publication apparaîtra aussi dans la rubrique Témoignages.")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {TESTIMONY_CATS.map((c) => (
                        <button key={c.key} type="button" onClick={() => setTestimonyCat(c.key)} className={`chip text-xs ${testimonyCat === c.key ? "!bg-[#2D5A4C] !text-white !border-transparent" : ""}`} data-testid={`composer-testimony-cat-${c.key}`}>
                          {t(c.tKey, c.fb)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Lot 756 — « Où publier ? » : plusieurs groupes (+ mon fil) en une écriture */}
            {multiGroupsOn && (
              <div className="mt-3 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid="composer-destinations" data-count={destCount}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold flex items-center gap-1.5 min-w-0"><Users size={16} style={{ color: "var(--yalola-primary)" }} /> {t("composer.groups_title", "Où publier ?")}</span>
                  <button type="button" onClick={() => setGroupsSheet(true)} className="chip text-xs" data-testid="composer-destinations-open">{t("composer.groups_choose", "Choisir")}</button>
                </div>
                <div className="text-xs mt-1 truncate" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-destinations-label">{destLabel}</div>
              </div>
            )}
            {/* Lot 649 — audience (PC / tablette) : chips façon « Publier en tant que » */}
            {audienceEnabled && (
              <div className="mt-3 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid="composer-audience" data-audience={audience}>
                <div className="text-sm font-semibold flex items-center gap-1.5 mb-1.5">{React.createElement(audienceOf(audience).icon, { size: 16, style: { color: "var(--yalola-primary)" } })} {t("audience.title", "Qui peut voir ta publication ?")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {AUDIENCES.map((a) => (
                    <button key={a.key} type="button" onClick={() => setAudience(a.key)} aria-pressed={audience === a.key} className="chip transition-colors flex items-center gap-1"
                      style={audience === a.key ? { background: audienceAccent(a.key), color: "#fff", borderColor: "transparent" } : { background: "var(--yalola-surface-alt)", color: a.accent || "var(--yalola-text)" }}
                      title={t(a.dkey, a.desc)} data-testid={`composer-audience-${a.key}`}>
                      <a.icon size={13} /> {t(a.tkey, a.label)}
                    </button>
                  ))}
                </div>
                {audience === "close_friends" && <div className="mt-2"><CloseFriendsLink onClick={() => setCloseFriendsOpen(true)} testId="composer-close-friends-link" /></div>}
                <CloseFriendsDialog open={closeFriendsOpen} onOpenChange={setCloseFriendsOpen} />
              </div>
            )}
            {/* Schedule for later */}
            <div className="mt-3 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock size={16} style={{ color: "var(--yalola-primary)" }} /> {t("post.schedule", "Programmer la publication")}</span>
                <Switch checked={scheduleOn} onCheckedChange={(v) => { setScheduleOn(v); if (!v) setScheduledAt(""); }} data-testid="composer-schedule-switch" />
              </div>
              {scheduleOn && (
                <input type="datetime-local" value={scheduledAt} min={minDatetimeLocal()} max={maxDatetimeLocal()} onChange={(e) => setScheduledAt(e.target.value)}
                  className="mt-2 w-full rounded-lg border px-3 py-1.5 text-sm outline-none" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="composer-schedule-input" />
              )}
            </div>
            </div>
           </div>
          </DialogBody>
          {isMobileSheet && emojiTray && <EmojiTray onEmoji={insertEmoji} onClose={() => setEmojiTray(false)} t={t} />}
          <DialogFooter className="sm:flex-row sm:items-stretch">
            {previewOn && (
            <Button
              variant="outline"
              className="rounded-xl h-11 sm:w-auto"
              onClick={openPreview}
              disabled={posting || (!content.trim() && !hasMedia)}
              data-testid="composer-preview"
            >
              <Eye size={16} className="mr-1.5" /> {t("post.preview_btn", "Aperçu")}
            </Button>
            )}
            <Button
              className="w-full sm:flex-1 pill-primary rounded-xl h-11"
              onClick={doPublish}
              disabled={!canPublish}
              data-testid="composer-publish"
            >
              {posting ? "Publication..." : publishLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!phoneFs && previewOn && <PostPreviewDialog open={!!previewPost} onOpenChange={(o) => { if (!o) setPreviewPost(null); }} post={previewPost} onPublish={publishFromPreview} publishLabel={publishLabel} t={t} />}
      {multiGroupsOn && <ComposerGroupsSheet open={groupsSheet} onOpenChange={setGroupsSheet} value={destinations} onChange={setDestinations} lockedGroupId={groupId || null} allowPersonal={!groupId} />}
    </div>
  );
};
